/** AI-CALL-AUDIT-1C R1 — the last thing the user clicked before an AI call.
 *
 * Never records input/textarea/select values or anything typed — only a
 * button/link's own label. Installed once on import via a capturing document
 * listener so it sees clicks regardless of stopPropagation() further down
 * the tree.
 */
interface ClickRecord {
  label: string
  at: number
}

let _last: ClickRecord | null = null

/** Collapse a label to the ByteString range the X-Yansar-Action header
 *  requires (char codes 0x20-0x7E) — see AI-CALL-AUDIT-1C FIX-1. A label
 *  with nothing left after cleaning (e.g. a bare glyph) becomes null. */
function _headerSafe(s: string): string | null {
  const t = s
    .normalize('NFKD')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(/[^\x20-\x7E]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80)
  return t || null
}

function _labelFor(el: Element): string | null {
  const dataAction = el.getAttribute('data-audit-action')
  if (dataAction) return _headerSafe(dataAction)

  const ariaLabel = el.getAttribute('aria-label')
  if (ariaLabel) return _headerSafe(ariaLabel)

  const text = (el.textContent ?? '').replace(/\s+/g, ' ').trim()
  return text ? _headerSafe(text) : null
}

function _handler(event: MouseEvent): void {
  const target = event.target as Element | null
  const el = target?.closest('button, a, [role="button"], [data-audit-action]')
  if (!el) return

  const label = _labelFor(el)
  if (!label) return

  _last = { label, at: Date.now() }
}

document.addEventListener('click', _handler, { capture: true })

/** The label of the last matching click, or null if none happened within
 *  `maxAgeMs`. */
export function recentAction(maxAgeMs = 10_000): string | null {
  if (!_last) return null
  if (Date.now() - _last.at > maxAgeMs) return null
  return _last.label
}
